import { PermissionRequest, Settings, SetUserRole, UserRole, } from '@streamr/tv-engine';
import { areEqualBinaries, binaryToHex, binaryToUtf8, toUserId, toUserIdRaw, utf8ToBinary, } from '@streamr/utils';
import { EventEmitter } from 'eventemitter3';
import { decrypt, encrypt } from '../helpers/EcdhEncryption';
import { UserStore } from './UserStore';
const ONE_TO_ONE_STREAM_DOMAIN = 'tv.streamr.eth';
function generateOneToOneStreamId() {
    const randomPart = Math.random().toString(36).substring(2);
    return `${ONE_TO_ONE_STREAM_DOMAIN}/1to1-${randomPart}`;
}
function generateOneToOneAesKey() {
    return crypto.getRandomValues(new Uint8Array(32));
}
function buildOneToOneStreamUrl(streamId, aesKey) {
    return `${streamId}#${binaryToHex(aesKey, false)}`;
}
function truncateUserId(userId) {
    if (userId.length <= 10) {
        return userId;
    }
    return `${userId.slice(0, 6)}...${userId.slice(-4)}`;
}
export class UserManager extends EventEmitter {
    hostUserId;
    ownUserId;
    ownPrivateKey;
    users = new Map();
    outgoingRequests = new Set();
    usersByStatus = new Map([
        [UserRole.VIEWER, new Set()],
        [UserRole.SPEAKER, new Set()],
        [UserRole.COHOST, new Set()],
        [UserRole.GUEST, new Set()],
        [UserRole.CAMERA, new Set()],
    ]);
    userStore;
    streamId;
    // Users already checked for post-reload re-acceptance this session.
    reAcceptCheckedUsers = new Set();
    // One-to-one stream URL per accepted guest, kept so a re-accept (guest
    // browser reload) reuses the stream the N-1 publisher already targets.
    oneToOneUrlsByUser = new Map();
    // Injected by StreamrAppApi: whether media from the user is currently
    // arriving. Gates one-to-one stream reuse on accept.
    isUserMediaFlowing;
    heartbeatDisplayNames = new Map();
    // Self-reported WebRTC transport summaries (from heartbeats; own entry is
    // fed locally). Parallel map, not on the User record: plain viewers are
    // filtered out of the roster but still self-report.
    connectionSummaries = new Map();
    // Network node id of each participant (from heartbeats). Used to scope
    // the local connection summary to session peers.
    heartbeatNodeIds = new Map();
    localDisplayName;
    ownStatus = UserRole.VIEWER;
    speakerSecret;
    hostSecret;
    streamrWrapper;
    heartbeatMonitorInterval;
    pendingSecretRequest;
    constructor(hostUserId, ownUserId, ownPrivateKey, speakerSecret, hostSecret, streamId) {
        super();
        this.hostUserId = hostUserId;
        this.ownUserId = ownUserId;
        this.ownPrivateKey = ownPrivateKey;
        this.streamId = streamId;
        this.userStore = new UserStore();
        this.localDisplayName = this.userStore.getDisplayName();
        if (this.isHost(this.ownUserId)) {
            this.speakerSecret = crypto.getRandomValues(new Uint8Array(32));
            this.hostSecret = crypto.getRandomValues(new Uint8Array(32));
            this.ownStatus = UserRole.COHOST;
            this.emitOwnRoleChanged(UserRole.COHOST);
        }
        else {
            this.speakerSecret = speakerSecret;
            this.hostSecret = hostSecret;
            const persistedRole = this.userStore.getUserRole(this.hostUserId);
            if (persistedRole !== undefined &&
                persistedRole !== UserRole.VIEWER &&
                speakerSecret) {
                this.ownStatus = persistedRole;
                const storedOneToOneUrl = this.userStore.getOneToOneStreamUrl(this.hostUserId);
                console.log('[UserManager] Restoring persisted role from localStorage', {
                    persistedRole,
                    hasSpeakerSecret: !!speakerSecret,
                    storedOneToOneUrl: storedOneToOneUrl?.slice(0, 30),
                });
                this.emitOwnRoleChanged(persistedRole, storedOneToOneUrl);
            }
            else if (hostSecret) {
                void this.requestPermission(UserRole.COHOST);
            }
            else if (speakerSecret) {
                void this.requestPermission(UserRole.SPEAKER);
            }
        }
    }
    // --- User map helpers ---
    getOrCreateUser(userId) {
        if (userId === this.ownUserId) {
            throw new Error('Local user must not be added to the users map');
        }
        let user = this.users.get(userId);
        if (!user) {
            user = {
                id: userId,
                displayName: undefined,
                status: UserRole.VIEWER,
                online: true,
                incomingRequest: undefined,
                lastHeartbeatTimeMillis: Date.now(),
                uploadRateBytesPerSecond: 0,
                downloadRateBytesPerSecond: 0,
                neighborCount: 0,
                isRecording: false,
            };
            this.users.set(userId, user);
            this.usersByStatus.get(UserRole.VIEWER)?.add(userId);
            this.emit('userCameOnline', userId, user.displayName ?? userId);
        }
        else if (!user.online) {
            user.online = true;
            user.lastHeartbeatTimeMillis = Date.now();
            this.emit('userCameOnline', userId, user.displayName ?? userId);
        }
        return user;
    }
    changeUserRole(userId, newStatus) {
        const user = this.users.get(userId);
        if (!user) {
            return;
        }
        const oldStatus = user.status;
        if (oldStatus === newStatus) {
            return;
        }
        this.usersByStatus.get(oldStatus)?.delete(userId);
        this.usersByStatus.get(newStatus)?.add(userId);
        user.status = newStatus;
        this.emit('userRoleChanged', userId, newStatus, user.displayName ?? userId);
    }
    handleUserDisconnect(userId) {
        this.heartbeatNodeIds.delete(userId);
        if (this.connectionSummaries.delete(userId)) {
            this.emit('connectionSummariesChanged', this.getConnectionSummaries());
        }
        const user = this.users.get(userId);
        if (!user) {
            return;
        }
        const hasElevatedRole = user.status !== UserRole.VIEWER && user.status !== UserRole.BANNED;
        if (hasElevatedRole) {
            user.online = false;
            this.emit('userWentOffline', userId);
            return;
        }
        this.usersByStatus.get(user.status)?.delete(userId);
        this.users.delete(userId);
        this.emit('userWentOffline', userId);
        this.emit('userCountChanged', this.users.size);
        if (user.displayName !== undefined) {
            this.emitDisplayNamesChanged();
        }
        if (user.incomingRequest !== undefined) {
            this.emitIncomingRequestsChanged();
        }
    }
    setIsUserMediaFlowing(check) {
        this.isUserMediaFlowing = check;
    }
    // --- Display name management ---
    setLocalDisplayName(name) {
        this.localDisplayName = name;
        this.userStore.setDisplayName(name);
        this.emitDisplayNamesChanged();
    }
    getLocalDisplayName() {
        return this.localDisplayName;
    }
    updateRemoteDisplayName(userId, displayName) {
        if (userId === this.ownUserId) {
            return;
        }
        const user = this.users.get(userId);
        if (user) {
            if (user.displayName !== displayName) {
                user.displayName = displayName;
                this.emitDisplayNamesChanged();
            }
        }
    }
    resolveDisplayName(userId) {
        if (userId === this.ownUserId) {
            return this.localDisplayName || truncateUserId(userId);
        }
        const user = this.users.get(userId);
        return (user?.displayName ||
            this.heartbeatDisplayNames.get(userId) ||
            truncateUserId(userId));
    }
    getDisplayNames() {
        const result = new Map();
        for (const [userId, name] of this.heartbeatDisplayNames) {
            result.set(userId, name);
        }
        for (const user of this.users.values()) {
            if (user.displayName !== undefined) {
                result.set(user.id, user.displayName);
            }
        }
        if (this.localDisplayName) {
            result.set(this.ownUserId, this.localDisplayName);
        }
        return result;
    }
    emitDisplayNamesChanged() {
        this.emit('displayNamesChanged', this.getDisplayNames());
    }
    getConnectionSummaries() {
        return new Map(this.connectionSummaries);
    }
    /** Network node ids of the session's participants (from heartbeats). */
    getSessionNodeIds() {
        return [...new Set(this.heartbeatNodeIds.values())];
    }
    /** The local client's own summary (its heartbeats are not received back). */
    setLocalConnectionSummary(summary) {
        this.updateConnectionSummary(this.ownUserId, summary);
    }
    updateConnectionSummary(userId, summary) {
        const prev = this.connectionSummaries.get(userId);
        if (prev?.webrtcDirectConnectionCount ===
            summary.webrtcDirectConnectionCount &&
            prev?.webrtcRelayConnectionCount ===
                summary.webrtcRelayConnectionCount) {
            return;
        }
        this.connectionSummaries.set(userId, summary);
        this.emit('connectionSummariesChanged', this.getConnectionSummaries());
    }
    // --- Heartbeat integration ---
    handleHeartbeat(publisherId, heartbeat) {
        if (publisherId === this.ownUserId) {
            return;
        }
        if (heartbeat.displayName) {
            const prev = this.heartbeatDisplayNames.get(publisherId);
            if (prev !== heartbeat.displayName) {
                this.heartbeatDisplayNames.set(publisherId, heartbeat.displayName);
                this.emitDisplayNamesChanged();
            }
        }
        if (heartbeat.nodeId !== '') {
            this.heartbeatNodeIds.set(publisherId, heartbeat.nodeId);
        }
        if (heartbeat.webrtcDirectConnectionCount !== undefined &&
            heartbeat.webrtcRelayConnectionCount !== undefined) {
            this.updateConnectionSummary(publisherId, {
                webrtcDirectConnectionCount: heartbeat.webrtcDirectConnectionCount,
                webrtcRelayConnectionCount: heartbeat.webrtcRelayConnectionCount,
            });
        }
        if (this.pendingSecretRequest !== undefined) {
            const pendingStatus = this.pendingSecretRequest;
            this.pendingSecretRequest = undefined;
            void this.requestPermission(pendingStatus);
        }
        // Host reload recovery: a freshly-reloaded host has an empty
        // roster, so a previously-accepted participant shows up as a
        // plain VIEWER when its heartbeat arrives. Re-accept it with the
        // persisted role — the accept flow re-issues the one-to-one
        // stream (SetUserRole), which the guest's role-change handler
        // consumes to rejoin and restore its N-1 audio.
        if (this.isHost(this.ownUserId) &&
            this.streamId &&
            !this.reAcceptCheckedUsers.has(publisherId)) {
            this.reAcceptCheckedUsers.add(publisherId);
            const storedRole = this.userStore.getHostAcceptedUsers(this.streamId)[publisherId];
            if (storedRole !== undefined) {
                const user = this.getOrCreateUser(publisherId);
                if (user.status === UserRole.VIEWER) {
                    console.log(`[UserManager] re-accepting previously accepted participant ${truncateUserId(publisherId)} (role ${storedRole}) after host reload`);
                    void this.acceptPermission(publisherId, storedRole);
                }
            }
        }
        const existingUser = this.users.get(publisherId);
        if (existingUser) {
            // A user we previously timed out (e.g. a guest that dropped off the
            // network) is sending heartbeats again — bring it back online and
            // announce it, so the host re-composites the returning participant.
            // Without this, an ungraceful drop (no Leave message) leaves the
            // user pinned offline forever and its video never reappears.
            if (!existingUser.online) {
                existingUser.online = true;
                this.emit('userCameOnline', publisherId, existingUser.displayName ?? publisherId);
            }
            existingUser.lastHeartbeatTimeMillis = Date.now();
            existingUser.uploadRateBytesPerSecond =
                heartbeat.uploadRateBytesPerSecond;
            existingUser.downloadRateBytesPerSecond =
                heartbeat.downloadRateBytesPerSecond;
            existingUser.neighborCount = heartbeat.neighbors.length;
            existingUser.isRecording = heartbeat.isRecording;
            return;
        }
        const isHostOrCohost = this.ownStatus === UserRole.COHOST || this.isHost(this.ownUserId);
        if (!isHostOrCohost) {
            return;
        }
        const user = this.getOrCreateUser(publisherId);
        user.uploadRateBytesPerSecond = heartbeat.uploadRateBytesPerSecond;
        user.downloadRateBytesPerSecond = heartbeat.downloadRateBytesPerSecond;
        user.neighborCount = heartbeat.neighbors.length;
        user.isRecording = heartbeat.isRecording;
        this.emit('userCountChanged', this.users.size);
    }
    startHeartbeatMonitor() {
        if (this.heartbeatMonitorInterval) {
            return;
        }
        this.heartbeatMonitorInterval = setInterval(() => {
            const now = Date.now();
            for (const [userId, user] of this.users.entries()) {
                const hasElevatedRole = user.status === UserRole.SPEAKER ||
                    user.status === UserRole.COHOST ||
                    user.status === UserRole.GUEST ||
                    user.status === UserRole.CAMERA;
                const expirationMs = hasElevatedRole
                    ? Settings.speakerHeartbeatExpiration
                    : Settings.defaultHeartbeatInterval;
                if (user.online &&
                    now - user.lastHeartbeatTimeMillis > expirationMs) {
                    this.handleUserDisconnect(userId);
                }
            }
        }, Settings.viewerCounterUpdateInterval);
    }
    stopHeartbeatMonitor() {
        if (this.heartbeatMonitorInterval) {
            clearInterval(this.heartbeatMonitorInterval);
            this.heartbeatMonitorInterval = undefined;
        }
    }
    // --- ViewerCounter-equivalent methods (host/cohost only) ---
    getViewerCount() {
        return this.users.size;
    }
    getUploadRates() {
        return Array.from(this.users.values()).map((u) => u.uploadRateBytesPerSecond);
    }
    getDownloadRates() {
        return Array.from(this.users.values()).map((u) => u.downloadRateBytesPerSecond);
    }
    getNeighborCounts() {
        return Array.from(this.users.values()).map((u) => u.neighborCount);
    }
    // --- Permission management ---
    isHost(userId) {
        return userId === this.hostUserId;
    }
    isHostOrCohost(userId) {
        if (userId === this.ownUserId) {
            return this.isHost(userId) || this.ownStatus === UserRole.COHOST;
        }
        const user = this.users.get(userId);
        return (this.isHost(userId) ||
            (user !== undefined && user.status === UserRole.COHOST));
    }
    getSecretForStatus(status) {
        return status === UserRole.SPEAKER
            ? this.speakerSecret
            : this.hostSecret;
    }
    async encryptForUser(userId, data) {
        return await encrypt(data, toUserIdRaw(userId));
    }
    requestKey(userId, status) {
        return `${userId}:${status}`;
    }
    emitOwnRoleChanged(userRole, oneToOneStreamUrl) {
        this.emit('ownRoleChanged', userRole, oneToOneStreamUrl);
    }
    emitIncomingRequestsChanged() {
        const requests = [];
        for (const user of this.users.values()) {
            if (user.incomingRequest !== undefined) {
                requests.push({
                    userId: user.id,
                    userRole: user.incomingRequest,
                });
            }
        }
        this.emit('incomingRequestsChanged', requests);
    }
    emitOutgoingRequestsChanged() {
        const requests = [];
        for (const requestKey of this.outgoingRequests) {
            const [userIdStr, statusStr] = requestKey.split(':');
            const userId = userIdStr;
            const userRole = Number.parseInt(statusStr, 10);
            requests.push({ userId, userRole });
        }
        this.emit('outgoingRequestsChanged', requests);
    }
    getCurrentUserRole() {
        return this.ownStatus;
    }
    getCurrentUserId() {
        return this.ownUserId;
    }
    getIncomingRequests() {
        const requests = [];
        for (const user of this.users.values()) {
            if (user.incomingRequest !== undefined) {
                requests.push({
                    userId: user.id,
                    userRole: user.incomingRequest,
                });
            }
        }
        return requests;
    }
    getStreamrWrapper() {
        return this.streamrWrapper;
    }
    async requestPermission(userRole) {
        const secret = this.getSecretForStatus(userRole);
        this.outgoingRequests.add(this.requestKey(this.ownUserId, userRole));
        this.emitOutgoingRequestsChanged();
        this.streamrWrapper?.publishPermissionRequest(PermissionRequest.create({
            userRole,
            secret: secret
                ? await this.encryptForUser(this.hostUserId, secret)
                : undefined,
            displayName: this.localDisplayName,
        }));
    }
    async acceptPermission(userId, permission, force = false) {
        const user = this.getOrCreateUser(userId);
        if (!force && user.status === permission) {
            return;
        }
        const userIdRaw = toUserIdRaw(userId);
        this.changeUserRole(userId, permission);
        user.incomingRequest = undefined;
        this.emitIncomingRequestsChanged();
        // Persist the accepted role so a reloaded host can re-accept this
        // participant and the session recovers without any guest action.
        if (this.isHost(this.ownUserId) && this.streamId) {
            this.userStore.setHostAcceptedUser(this.streamId, userId, permission);
        }
        if (permission === UserRole.COHOST &&
            this.hostSecret &&
            this.speakerSecret) {
            this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
                targetUserId: userIdRaw,
                userRole: permission,
                hostSecret: await this.encryptForUser(userId, this.hostSecret),
                speakerSecret: await this.encryptForUser(userId, this.speakerSecret),
            }));
        }
        else if (permission === UserRole.GUEST && this.speakerSecret) {
            // Reuse the guest's existing one-to-one stream on re-accept
            // (e.g. after a browser reload the guest re-requests GUEST).
            // Minting a fresh stream per accept made the host's N-1
            // publisher and the guest's stored URL chase each other across
            // rounds, leaving the guest hearing itself (or nothing) until
            // the streams happened to converge.
            //
            // Exception: if the guest's media is NOT currently flowing to
            // us, the reused stream's part-topology is likely stuck (a
            // reloaded guest joins the old stream-part with a new node id,
            // and formation on the stale part sometimes never completes).
            // Rotate to a fresh stream instead — virgin stream parts form
            // reliably, like an initial join.
            let oneToOneStreamUrl = this.oneToOneUrlsByUser.get(userId);
            if (oneToOneStreamUrl &&
                this.isUserMediaFlowing !== undefined &&
                !this.isUserMediaFlowing(userId)) {
                console.log(`[UserManager] rotating one-to-one stream for ${userId.slice(0, 12)} — no media flowing on the reused stream`);
                oneToOneStreamUrl = undefined;
            }
            if (!oneToOneStreamUrl) {
                oneToOneStreamUrl = buildOneToOneStreamUrl(generateOneToOneStreamId(), generateOneToOneAesKey());
                this.oneToOneUrlsByUser.set(userId, oneToOneStreamUrl);
            }
            this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
                targetUserId: userIdRaw,
                userRole: permission,
                speakerSecret: await this.encryptForUser(userId, this.speakerSecret),
                oneToOneStreamUrl,
            }));
            this.emit('oneToOneStreamCreated', userId, oneToOneStreamUrl);
        }
        else if (permission === UserRole.SPEAKER && this.speakerSecret) {
            this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
                targetUserId: userIdRaw,
                userRole: permission,
                speakerSecret: await this.encryptForUser(userId, this.speakerSecret),
            }));
        }
    }
    rejectPermission(userId, _permission) {
        const user = this.users.get(userId);
        if (user) {
            user.incomingRequest = undefined;
        }
        this.emitIncomingRequestsChanged();
        if (this.isHost(this.ownUserId) && this.streamId) {
            this.userStore.removeHostAcceptedUser(this.streamId, userId);
        }
        this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
            targetUserId: toUserIdRaw(userId),
            userRole: UserRole.VIEWER,
        }));
    }
    leaveStage() {
        if (this.ownStatus === UserRole.VIEWER) {
            return;
        }
        this.speakerSecret = undefined;
        this.hostSecret = undefined;
        this.ownStatus = UserRole.VIEWER;
        this.userStore.clearSpeakerSecret(this.hostUserId);
        this.userStore.clearUserRole(this.hostUserId);
        this.userStore.clearOneToOneStreamUrl(this.hostUserId);
        this.emitOwnRoleChanged(UserRole.VIEWER);
        this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
            targetUserId: toUserIdRaw(this.ownUserId),
            userRole: UserRole.VIEWER,
        }));
    }
    revokePermission(userId, _permission) {
        if (!this.hostSecret) {
            return;
        }
        if (this.isHost(this.ownUserId) && this.streamId) {
            this.userStore.removeHostAcceptedUser(this.streamId, userId);
        }
        this.changeUserRole(userId, UserRole.VIEWER);
        this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
            targetUserId: toUserIdRaw(userId),
            userRole: UserRole.VIEWER,
        }));
    }
    async sendPrivateMessage(targetUserId, message) {
        const binaryMessage = utf8ToBinary(message);
        const encryptedMessage = await encrypt(binaryMessage, toUserIdRaw(targetUserId));
        this.streamrWrapper?.publishPrivateMessage({
            data: encryptedMessage,
            targetUserId: toUserIdRaw(targetUserId),
        });
        this.emit('privateChatMessage', {
            senderId: targetUserId,
            message,
            timestamp: Date.now(),
            isSent: true,
        });
    }
    setStreamrWrapper(streamrWrapper) {
        this.streamrWrapper = streamrWrapper;
        this.streamrWrapper.on('permissionRequest', (permissionRequest, publisherId) => {
            void this.onPermissionRequest(toUserId(publisherId), permissionRequest.userRole, permissionRequest.secret, permissionRequest.displayName);
        });
        this.streamrWrapper.on('setUserRole', (setUserRole) => {
            void this.onSetUserRole(toUserId(setUserRole.targetUserId), setUserRole.userRole, setUserRole.speakerSecret, setUserRole.hostSecret, setUserRole.oneToOneStreamUrl);
        });
        this.streamrWrapper.on('privateMessage', (privateMessage, publisherId) => {
            if (areEqualBinaries(privateMessage.targetUserId, toUserIdRaw(this.ownUserId))) {
                void this.onPrivateMessage(toUserId(publisherId), privateMessage.data);
            }
        });
        this.streamrWrapper.on('leave', (_leave, publisherId) => {
            this.onLeave(toUserId(publisherId));
        });
        this.startHeartbeatMonitor();
        if (!this.isHost(this.ownUserId)) {
            if (this.ownStatus === UserRole.VIEWER) {
                if (this.hostSecret) {
                    this.pendingSecretRequest = UserRole.COHOST;
                }
                else if (this.speakerSecret) {
                    this.pendingSecretRequest = UserRole.SPEAKER;
                }
            }
            else {
                // We restored a non-viewer role (GUEST/SPEAKER/COHOST) from a
                // previous session — e.g. a guest that dropped off the network
                // (airplane mode) and reloaded. Re-request that same role on
                // reconnect so the host re-establishes us; otherwise the host is
                // never told we're back and the session can't recover. The
                // request is sent once the first heartbeat arrives (see
                // onHeartbeat / pendingSecretRequest).
                this.pendingSecretRequest = this.ownStatus;
            }
        }
    }
    async onPermissionRequest(senderId, userRole, secret, displayName) {
        // A returning participant re-claiming a role it already holds on our
        // side (e.g. a guest that refreshed the page and re-requests after its
        // stored one-to-one stream timed out). Re-grant automatically —
        // regenerating the one-to-one stream for a GUEST — so it re-syncs and
        // resumes without the host having to manually approve. This only
        // matches an already-held elevated role, so a brand-new viewer/speaker
        // still cannot self-escalate to guest.
        if (this.isHostOrCohost(this.ownUserId)) {
            const existing = this.users.get(senderId);
            if (existing &&
                existing.status === userRole &&
                userRole !== UserRole.VIEWER) {
                if (displayName) {
                    this.updateRemoteDisplayName(senderId, displayName);
                }
                await this.acceptPermission(senderId, userRole, true);
                return;
            }
        }
        let autoAccepted = false;
        if (secret && this.speakerSecret && this.hostSecret) {
            const validationSecret = this.getSecretForStatus(userRole);
            if (!validationSecret) {
                throw new Error('Assertion error: validationSecret is undefined');
            }
            try {
                const decryptedSecret = await decrypt(this.ownPrivateKey, secret);
                const isValid = areEqualBinaries(decryptedSecret, validationSecret);
                if (isValid) {
                    await this.acceptPermission(senderId, userRole);
                    autoAccepted = true;
                }
                // On an INVALID secret we deliberately do NOT auto-reject here.
                // rejectPermission() publishes SetUserRole(VIEWER), which demotes
                // the requester and clears its secrets — destructive and
                // unrecoverable for a returning participant whose persisted
                // secret no longer matches (e.g. after the host regenerated
                // secrets, or a reconnect). Instead we fall through to the manual
                // incoming-request list below so the host can approve (re-grant).
            }
            catch (_e) { }
        }
        if (!autoAccepted && this.isHostOrCohost(this.ownUserId)) {
            const user = this.getOrCreateUser(senderId);
            if (displayName) {
                this.updateRemoteDisplayName(senderId, displayName);
            }
            user.incomingRequest = userRole;
            this.emitIncomingRequestsChanged();
        }
    }
    async onSetUserRole(targetUserId, userRole, speakerSecret, hostSecret, oneToOneStreamUrl) {
        if (targetUserId === this.ownUserId) {
            this.outgoingRequests.delete(this.requestKey(this.ownUserId, UserRole.SPEAKER));
            this.outgoingRequests.delete(this.requestKey(this.ownUserId, UserRole.COHOST));
            this.outgoingRequests.delete(this.requestKey(this.ownUserId, UserRole.GUEST));
            this.emitOutgoingRequestsChanged();
            if (userRole === UserRole.VIEWER) {
                this.speakerSecret = undefined;
                this.hostSecret = undefined;
                this.ownStatus = UserRole.VIEWER;
                this.userStore.clearSpeakerSecret(this.hostUserId);
                this.userStore.clearUserRole(this.hostUserId);
                this.userStore.clearOneToOneStreamUrl(this.hostUserId);
                this.emitOwnRoleChanged(UserRole.VIEWER);
            }
            else {
                try {
                    if (speakerSecret) {
                        this.speakerSecret = await decrypt(this.ownPrivateKey, speakerSecret);
                        this.userStore.setSpeakerSecret(this.hostUserId, this.speakerSecret);
                    }
                    if (hostSecret) {
                        this.hostSecret = await decrypt(this.ownPrivateKey, hostSecret);
                    }
                }
                catch (error) {
                    console.error('[UserManager] Failed to decrypt secrets in onSetUserRole:', error);
                    return;
                }
                this.ownStatus = userRole;
                this.userStore.setUserRole(this.hostUserId, userRole);
                if (oneToOneStreamUrl) {
                    this.userStore.setOneToOneStreamUrl(this.hostUserId, oneToOneStreamUrl);
                }
                this.emitOwnRoleChanged(userRole, oneToOneStreamUrl);
            }
        }
        else {
            if (userRole === UserRole.VIEWER) {
                const user = this.users.get(targetUserId);
                if (user) {
                    this.changeUserRole(targetUserId, UserRole.VIEWER);
                }
                // The participant stepped down (or was revoked): forget the
                // persisted acceptance so a host reload doesn't re-promote
                // them, and allow a future re-request to be re-accepted.
                if (this.isHost(this.ownUserId) && this.streamId) {
                    this.userStore.removeHostAcceptedUser(this.streamId, targetUserId);
                }
            }
            else {
                this.getOrCreateUser(targetUserId);
                this.changeUserRole(targetUserId, userRole);
            }
        }
    }
    async onPrivateMessage(senderId, data) {
        let decryptedMessage;
        try {
            decryptedMessage = await decrypt(this.ownPrivateKey, data);
        }
        catch (error) {
            console.error('[UserManager] Failed to decrypt private message from', senderId, error);
            return;
        }
        const message = binaryToUtf8(decryptedMessage);
        const chatMessage = {
            senderId,
            message,
            timestamp: Date.now(),
        };
        this.emit('privateChatMessage', chatMessage);
    }
    onLeave(userId) {
        this.handleUserDisconnect(userId);
    }
    getSpeakers() {
        const result = Array.from(this.usersByStatus.get(UserRole.SPEAKER) ?? []);
        if (this.ownStatus === UserRole.SPEAKER) {
            result.push(this.ownUserId);
        }
        return result;
    }
    getCohosts() {
        const result = Array.from(this.usersByStatus.get(UserRole.COHOST) ?? []);
        if (this.ownStatus === UserRole.COHOST &&
            !this.isHost(this.ownUserId)) {
            result.push(this.ownUserId);
        }
        return result;
    }
    getSpeakerRequests() {
        const result = [];
        for (const user of this.users.values()) {
            if (user.incomingRequest === UserRole.SPEAKER) {
                result.push(user.id);
            }
        }
        return result;
    }
    getCohostRequests() {
        const result = [];
        for (const user of this.users.values()) {
            if (user.incomingRequest === UserRole.COHOST) {
                result.push(user.id);
            }
        }
        return result;
    }
    getGuests() {
        const result = Array.from(this.usersByStatus.get(UserRole.GUEST) ?? []);
        if (this.ownStatus === UserRole.GUEST) {
            result.push(this.ownUserId);
        }
        return result;
    }
    getCameras() {
        return Array.from(this.usersByStatus.get(UserRole.CAMERA) ?? []);
    }
    getGuestRequests() {
        const result = [];
        for (const user of this.users.values()) {
            if (user.incomingRequest === UserRole.GUEST) {
                result.push(user.id);
            }
        }
        return result;
    }
    getOutgoingRequests() {
        return Array.from(this.outgoingRequests).map((key) => {
            const [userIdStr, statusStr] = key.split(':');
            const userId = userIdStr;
            const userRole = Number.parseInt(statusStr, 10);
            return { userId, userRole };
        });
    }
    getUser(userId) {
        return this.users.get(userId);
    }
    getUsers() {
        return this.users;
    }
}
